<template>
  <div :class="$style.loginContainer">
    <div :class="$style.frameLogin">
      <div :class="$style.frameParent">
        <div :class="$style.logo1Wrapper">
          <img :class="$style.logo1Icon" alt="" src="@/assets/logoYellow.svg" />
        </div>
        <div :class="$style.register">{{ $t('message.login') }}</div>
        <div :class="$style.loadingContainer">
          <LoadingSpinner :isLoading="state === LoginViewFrameState.Loading || state === LoginViewFrameState.Redirect
            " />
          <div v-if="state === LoginViewFrameState.Error" :class="$style.error">
            {{ $t(errorState) }}
          </div>
        </div>
        <div :class="$style.buttonBigNegWrapper">
          <ButtonPrimaryBigNeg :button-title="$t('message.login')" @handle-click="handleLogin"
            :disabled="state != LoginViewFrameState.Initial" />
        </div>

        <!-- register feature not yet functional -->
        <!--<div :class="$style.forgotPassword">
          <div @click="register">{{ $t('message.orRegister') }}</div>
        </div>-->
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { authService } from '../services/authentication'
import LoadingSpinner from '../LoadingSpinner.vue'
import ButtonPrimaryBigNeg from '@/components/buttons/buttonPrimaryBigNeg.vue'
import { useLocalStorage } from '@vueuse/core'
import { LoginViewFrameState } from './LoginViewLoginFrameTypes'

export default defineComponent({
  name: 'LoginViewLoginFrame',
  components: {
    LoadingSpinner,
    ButtonPrimaryBigNeg
  },
  setup() {
    const store = useLocalStorage('login-storage', {
      hasRedirected: false
    })
    const registerUrl = import.meta.env.VITE_REGISTER_URL
    const errorState = ref('message.server_error')

    const errorText = ref<string>('message.server_error')
    const router = useRouter()
    const state = ref<LoginViewFrameState>(LoginViewFrameState.Initial)
    const isLoading = ref(false)
    const submit = () => {
      isLoading.value = !isLoading.value
    }

    const handleLogin = () => {
      changeState(LoginViewFrameState.Loading)
      window.location.href = registerUrl
      store.value.hasRedirected = true
    }

    const register = () => {
      router.replace('/register')
    }

    const isStatusCodeInRange = (statusCode: number, range: [number, number]): boolean =>
      statusCode >= range[0] && statusCode <= range[1]
    const validateStatus = (statusCode: string): string => {
      const status = parseInt(statusCode)
      switch (true) {
        case isStatusCodeInRange(status, [400, 499]):
          return 'message.unauthorized_access'
        case isStatusCodeInRange(status, [500, 599]):
          return 'message.server_error'
        default:
          // TODO localized error message
          return 'message.server_error'
      }
    }
    const cleanErrorAfter = (milliseconds: number) => {
      let timer = 1
      let interval = setInterval(() => {
        if (timer === 0) {
          clearInterval(interval)
          changeState(LoginViewFrameState.Initial)
        } else {
          timer--
        }
      }, milliseconds)
    }
    const nextState = async () => {
      switch (state.value) {
        case LoginViewFrameState.Initial:
          state.value = LoginViewFrameState.Loading
          break
        case LoginViewFrameState.Loading:
          state.value = LoginViewFrameState.Error
          break
        case LoginViewFrameState.Error:
          state.value = LoginViewFrameState.Initial
          break
      }
    }
    const changeState = async (newState: LoginViewFrameState) => {
      state.value = newState
    }
    const error = async () => {
      changeState(LoginViewFrameState.Error)
      cleanErrorAfter(2000)
    }
    const authtenticate = async (code: string) => {
      try {
        isLoading.value = true
        const result = await authService.authenticate(code)
        if (result.isOk()) {
          isLoading.value = false
          router.push('/user')
          changeState(LoginViewFrameState.Initial)
        } else {
          const errorText = result.value() as string
          errorState.value = validateStatus(errorText)
          isLoading.value = false
          error()
        }
      } catch (err) {
        errorText.value = 'Login failed. Please check your credentials.'
        isLoading.value = false
      }
    }
    const getCodeFromQuery = (urlParams: URLSearchParams): string | null => {
      const code: string | null = urlParams.get('code')
      return code
    }

    const clearBrowserAddress = (urlParams: URLSearchParams) => {
      const currentUrl = new URL(window.location.href)
      currentUrl.search = urlParams.toString()
      window.history.replaceState({}, '', currentUrl.toString())
    }

    onMounted(() => {
      if (store.value.hasRedirected !== true) {
        return
      }
      const urlParams = new URLSearchParams(window.location.search)
      const code = getCodeFromQuery(urlParams)
      if (code != null) {
        changeState(LoginViewFrameState.Loading)
        urlParams.delete('code')
        clearBrowserAddress(urlParams)
        authtenticate(code)
        store.value.hasRedirected = false
      } else {
        errorState.value = validateStatus('500')
        error()
        store.value.hasRedirected = false
      }
    })

    return {
      LoginViewFrameState,
      state,
      registerUrl,
      handleLogin,
      register,
      nextState,
      errorState,
      isLoading,
      submit
    }
  }
})
</script>

<style module>
.logo1Icon {
  width: 127.9px;
  position: relative;
  height: 50px;
  overflow: hidden;
  flex-shrink: 0;
}

.error {
  font-family: AntarlidaBold;
  font-size: medium;
  font-weight: 700;
  color: red;
}

.forgotPassword {
  position: relative;
  text-decoration: underline;
  line-height: 150%;
  font-weight: 600;
}

.logo1Wrapper {
  align-self: stretch;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
}

.operations {
  width: 316px;
  position: relative;
  line-height: 110%;
  font-weight: 600;
  display: inline-block;
  flex-shrink: 0;
}

.loadingContainer {
  width: 100%;
  height: 50px;
  position: relative;
  display: inline-block;
  background-color: transparent;
}

.buttonBigNegWrapper {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  padding: 16px 0px 8px;
  text-align: center;
  font-size: 26px;
  color: #f6f300;
  font-family: 'Antarlida';
}

.operationsWrapper {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  padding: 4px 0px;
  font-size: 20px;
}

.loginContainer {
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  justify-content: center;
  align-items: center;
  height: 100vh;
  background: url('@/assets/ouluport_background.png') no-repeat center center/cover;
  background-size: cover;
  background-position: center;
}

.frameParent {
  width: 100%;
  position: relative;
  background-color: var(--app-dark);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  padding: 40px 32px 32px;
  box-sizing: border-box;
  gap: 24px;
  text-align: left;
  font-size: 16px;
  color: #fff;
  font-family: 'Antarlida';
}

.frameLogin {
  margin: 0 auto;
  width: 380px;
  height: auto;
  position: relative;
}

/* Mobile */
@media (max-width: 768px) {
  .frameLogin {
    width: 320px;
  }
}

@media (hover: hover) {
  a:hover {
    color: var(--app-yellow) !important;
  }
}
</style>
